// The esv.org-style tools panel beside the reading column. "On this page" is
// built from the chapter's own sections; `children` is where the member's notes
// and bookmarks go; and the three cards below follow the verse the reader last
// clicked, so the rail is about the verse in front of them rather than the
// chapter in general.
//
// Everything in those three comes from public-domain text (phase 3): the ESV
// licence lets us keep 450 verses, and a cross-reference list or a concordance
// needs the whole canon.
import { toDisplay, parseRef, type ReaderSettings, type TranslationId } from "@bibleunleashed/shared";
import { CrossRefsCard } from "@/study/CrossRefsCard";
import { TranslationsCard } from "@/study/TranslationsCard";
import { WordStudyCard } from "@/study/WordStudyCard";

export interface OutlineEntry {
  id: string;
  heading: string;
}

/** The translation cross-reference previews are quoted in. */
const PREVIEW: TranslationId = "BSB";

export function StudyRail({
  outline,
  focus,
  parallel,
  onParallel,
  children,
}: {
  outline: OutlineEntry[];
  /** The verse the rail is about, e.g. "Neh.1.3", or null before one is picked. */
  focus: string | null;
  parallel: ReaderSettings["parallel"];
  onParallel: (value: ReaderSettings["parallel"]) => void;
  children?: React.ReactNode;
}) {
  const display = focus ? toDisplay(parseRef(focus)) : undefined;

  return (
    <aside className="min-w-0 space-y-8 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto">
      {outline.length > 0 && (
        <div>
          <h2 className="mb-3 font-sans text-[11px] tracking-[0.1em] text-ink-3 uppercase">On this page</h2>
          <ul className="space-y-2">
            {outline.map((entry, i) => (
              <li key={entry.id}>
                <a href={`#${entry.id}`} className="block truncate text-[14px] text-ink-2 hover:text-accent">
                  {entry.heading || `Section ${i + 1}`}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {children}
      <CrossRefsCard verse={focus} translation={PREVIEW} />
      <TranslationsCard verse={focus} display={display} parallel={parallel} onParallel={onParallel} />
      <WordStudyCard verse={focus} />
    </aside>
  );
}
